import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
} from '@datachain/ui-sdk/common';
import { OrderedMap } from 'immutable';

import { TagAdapter } from '../core/adapters/tag.adapter';
import { DcDxGrids } from '../core/dx-types';
import { DxListsStorage } from '../core/services/dx-lists.storage';
import {
	IDatablockExpositionsCount,
	IDatablockResponse,
} from '../requesters/datablock/datablock.model';
import {
	DatablockItemEntity,
	ExpositionsCount,
} from './components/datablock-list/datablock-item.entity';
import {
	DatablockListEntity,
	IDatablockListExtra,
} from './components/datablock-list/datablock-list.entity';

@Injectable()
export class DatablockAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath,
		private readonly dxListsStorage: DxListsStorage
	) {}

	public generateDatablockList(
		response: ReadonlyArray<IDatablockResponse>
	): DatablockListEntity {
		const list = this.generateDatablockListStructure();
		const items = response
			.reduce<Array<DatablockItemEntity>>((acc, item) => {
				const temp = this.generateDatablockItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});

		const orderedMap = OrderedMap<string, DatablockItemEntity>(
			items.map((item) => [`${item.id}`, item])
		);
		return list.setElements(orderedMap);
	}

	public generateDatablockListStructure(): DatablockListEntity {
		let availableCols: Array<IDatablockListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@datablock.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@datablock.list.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@datablock.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 4,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@datablock.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 5,
			},
			{
				field: 'persistenceState',
				alias: $localize`:i18n=@@datablock.list.column.persistence:`,
				type: '',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 6,
			},
			{
				field: 'expositionsCount',
				alias: $localize`:i18n=@@datablock.list.column.expositions:`,
				type: '',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: false,
				visibleIndex: 7,
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@datablock.list.column.creationDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 8,
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@datablock.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 9,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@datablock.list.column.updateDate:`,
				type: 'date',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 10,
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@datablock.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 11,
			},
		];

		availableCols = this.dxListsStorage.synchronize<
			IDatablockListExtra,
			DatablockItemEntity
		>(DcDxGrids.DatablockListStandalone, availableCols);
		return DatablockListEntity.build(availableCols);
	}

	private generateDatablockItemEntity(
		response: IDatablockResponse
	): DatablockItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		return DatablockItemEntity.build({
			id: response.id,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
			persistenceState: {
				recentJobId: response.status?.job_id ?? null,
				isPersisted: response.status?.persisted ?? false,
				date: response.status?.persistence_date
					? new Date(response.status?.persistence_date)
					: null,
				count: response.status?.current_repartition_number ?? 0,
				duration: response.status?.persistence_execution_time ?? 0,
				size: response.status?.persistence_size ?? 0,
			},
			isActionInProgress: response.status?.job_status === 'RUNNING',
		});
	}

	public updateListWithExpositionsCount(
		list: DatablockListEntity,
		result: ReadonlyArray<IDatablockExpositionsCount>
	): DatablockListEntity {
		return list.updateExpositionsCount(
			result.map((it) => [
				it.db_id,
				this.generateDatablockItemExpositionsCount(it),
			])
		);
	}

	private generateDatablockItemExpositionsCount(
		expositionsCount: Partial<IDatablockExpositionsCount>
	): ExpositionsCount {
		return {
			totalCount: expositionsCount?.total_count ?? 0,
			availableCount: expositionsCount?.available_count ?? 0,
		};
	}
}
